import React, { useEffect, useMemo, useState } from 'react';
import { CalendarDays, RefreshCcw, Save, Settings, Truck } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { supabase } from '../supabase';
import { useAuth } from '../context/AuthContext';
import {
  DEFAULT_STORE_LOGISTICS_CONFIG,
  getStoreLogisticsConfig,
  normalizeStoreThemeConfig,
} from '../utils/storeLogistics';

const STOREFRONT_REFRESH_EVENT = 'impulza:storefront-refresh';
const STOREFRONT_REFRESH_STORAGE_KEY = 'impulza:storefront-refresh';

export default function ConfigLogistica({ onNavigate }) {
  const { profile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState(null);

  useEffect(() => {
    if (!profile?.organization_id) return;
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile?.organization_id]);

  const logisticsConfig = useMemo(
    () => getStoreLogisticsConfig(config),
    [config],
  );

  const loadData = async () => {
    setLoading(true);
    try {
      const orgId = profile.organization_id;
      const { data, error } = await supabase
        .from('store_config')
        .select('organization_id, slug, store_title, theme_config')
        .eq('organization_id', orgId)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        const slugBase = `tienda-${orgId.slice(0, 6)}`;
        const { data: created, error: createError } = await supabase
          .from('store_config')
          .insert({
            organization_id: orgId,
            slug: slugBase,
            store_title: 'Mi Tienda Online',
            theme_config: { logistics: DEFAULT_STORE_LOGISTICS_CONFIG },
          })
          .select('organization_id, slug, store_title, theme_config')
          .single();

        if (createError) throw createError;
        setConfig({
          ...created,
          theme_config: normalizeStoreThemeConfig(created.theme_config),
        });
        return;
      }

      setConfig({
        ...data,
        theme_config: normalizeStoreThemeConfig(data.theme_config),
      });
    } catch (error) {
      console.error(error);
      toast.error('No se pudo cargar la configuracion de logistica.');
    } finally {
      setLoading(false);
    }
  };

  const updateLogistics = (patch) => {
    setConfig((prev) => ({
      ...prev,
      theme_config: {
        ...normalizeStoreThemeConfig(prev?.theme_config),
        logistics: {
          ...getStoreLogisticsConfig(prev),
          ...patch,
        },
      },
    }));
  };

  const save = async () => {
    if (!config || !profile?.organization_id) return;

    setSaving(true);
    try {
      const themeConfig = normalizeStoreThemeConfig(config.theme_config);

      const { error } = await supabase
        .from('store_config')
        .update({ theme_config: themeConfig })
        .eq('organization_id', profile.organization_id);

      if (error) throw error;

      const refreshPayload = {
        organizationId: profile.organization_id,
        slug: config.slug,
        ts: Date.now(),
      };

      localStorage.setItem(STOREFRONT_REFRESH_STORAGE_KEY, JSON.stringify(refreshPayload));
      window.dispatchEvent(new CustomEvent(STOREFRONT_REFRESH_EVENT, { detail: refreshPayload }));

      toast.success('Configuracion de logistica guardada.');
    } catch (error) {
      console.error(error);
      toast.error('No se pudieron guardar los cambios.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="h-screen flex flex-col items-center justify-center bg-[#F8FAFC]">
        <RefreshCcw className="animate-spin text-[#51A7FD] mb-4" size={36} />
        <p className="font-black uppercase tracking-[0.28em] text-slate-400 text-[10px]">
          Cargando logistica...
        </p>
      </div>
    );
  }

  if (!config) {
    return <div className="p-6 text-sm text-slate-500">No se pudo cargar la configuracion.</div>;
  }

  return (
    <div className="imp-page imp-container animate-nova-in">
      <div className="max-w-[1450px] mx-auto space-y-4">
        <div className="imp-header">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 bg-[#0B2A36] rounded-2xl flex items-center justify-center shadow-md shrink-0">
              <Truck size={18} className="text-[#3FF2AA]" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl md:text-2xl font-black text-slate-800 tracking-tight uppercase truncate">
                Config. Logistica
              </h1>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                Reglas del calendario logistico y planificacion operativa.
              </p>
            </div>
          </div>

          <div className="imp-header-actions">
            {typeof onNavigate === 'function' ? (
              <button
                type="button"
                onClick={() => onNavigate('logistica')}
                className="px-4 py-2.5 rounded-2xl bg-white border border-slate-200 text-slate-600 shadow-sm hover:shadow-md transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2"
              >
                <CalendarDays size={14} /> Ver calendario
              </button>
            ) : null}
            <button
              type="button"
              onClick={save}
              disabled={saving}
              className="px-5 py-2.5 rounded-2xl bg-[#0B2A36] text-white shadow-lg hover:bg-slate-800 transition-all text-[10px] font-black uppercase tracking-widest flex items-center gap-2 disabled:opacity-60"
            >
              {saving ? <RefreshCcw size={14} className="animate-spin" /> : <Save size={14} />}
              Guardar
            </button>
          </div>
        </div>

        <div className="relative overflow-hidden rounded-[28px] border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(63,242,170,0.16),_transparent_35%),linear-gradient(135deg,_#ffffff_0%,_#f8fbff_58%,_#eef6ff_100%)] shadow-sm">
          <div className="absolute -top-16 -right-10 h-40 w-40 rounded-full bg-[#51A7FD]/10 blur-3xl" />
          <div className="absolute bottom-0 left-0 h-28 w-28 rounded-full bg-[#3FF2AA]/10 blur-2xl" />

          <div className="relative p-5 md:p-6 lg:p-7">
            <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-4">
              <div className="max-w-2xl">
                <p className="text-[10px] font-black uppercase tracking-[0.28em] text-slate-400">
                  Centro de control
                </p>
                <h2 className="mt-2 text-2xl md:text-3xl font-black tracking-tight text-slate-900">
                  Defini como se programa cada pedido en operacion.
                </h2>
                <p className="mt-3 text-sm md:text-[15px] leading-relaxed text-slate-500">
                  Esta configuracion controla si el cliente puede elegir el dia del pedido y si el equipo logistico
                  puede reprogramarlo despues desde el calendario.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 xl:min-w-[560px]">
                <div className="rounded-[22px] border border-slate-200 bg-white/90 p-4 shadow-sm backdrop-blur">
                  <div className="flex items-center justify-between">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Estado</p>
                    <Settings size={16} className="text-slate-300" />
                  </div>
                  <p className={`mt-2 text-2xl font-black tracking-tight ${logisticsConfig.request_date_enabled ? 'text-emerald-600' : 'text-slate-800'}`}>
                    {logisticsConfig.request_date_enabled ? 'Activo' : 'Inactivo'}
                  </p>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                    Fecha del pedido en panel logistico
                  </p>
                </div>

                <div className="rounded-[22px] border border-slate-200 bg-white/90 p-4 shadow-sm backdrop-blur">
                  <div className="flex items-center justify-between">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Campo</p>
                    <CalendarDays size={16} className="text-[#51A7FD]" />
                  </div>
                  <p className="mt-2 text-2xl font-black tracking-tight text-slate-800">
                    Cliente + Logistica
                  </p>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                    Checkout y calendario operativo
                  </p>
                </div>

                <div className="rounded-[22px] border border-slate-200 bg-white/90 p-4 shadow-sm backdrop-blur">
                  <div className="flex items-center justify-between">
                    <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">Etiqueta</p>
                    <Truck size={16} className="text-slate-300" />
                  </div>
                  <p className="mt-2 text-lg font-black tracking-tight text-slate-800 break-words">
                    {logisticsConfig.request_date_label || 'Dia del pedido'}
                  </p>
                  <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                    Texto visible para operacion
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1.12fr)_360px] gap-4">
          <div className="bg-white rounded-[28px] border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 md:px-6 py-4 border-b border-slate-100 bg-white flex items-center gap-2">
              <Truck size={16} className="text-slate-400" />
              <span className="font-black text-slate-800 text-[11px] uppercase tracking-widest">
                Regla principal
              </span>
            </div>

            <div className="p-5 md:p-6 space-y-5">
              <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-4 items-stretch">
                <div className="rounded-[24px] border border-slate-200 bg-[linear-gradient(135deg,_#ffffff_0%,_#f8fafc_100%)] p-5">
                  <p className="text-[10px] font-black uppercase tracking-widest text-slate-500">
                    Habilitar campo "Dia del pedido"
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-slate-500 max-w-2xl">
                    Cuando este activo, el cliente puede elegir el dia para retiro o entrega durante la compra y
                    logistica tambien puede cambiarlo despues desde el calendario. Al mover la fecha, la orden se
                    reubica automaticamente en la vista mensual.
                  </p>

                  <div className="mt-4 flex flex-wrap gap-2">
                    <span className="px-3 py-1.5 rounded-full bg-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-500">
                      Reprogramacion inmediata
                    </span>
                    <span className="px-3 py-1.5 rounded-full bg-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-500">
                      Operacion interna
                    </span>
                    <span className="px-3 py-1.5 rounded-full bg-slate-100 text-[10px] font-black uppercase tracking-widest text-slate-500">
                      Calendario vivo
                    </span>
                  </div>
                </div>

                <div className={`rounded-[24px] border p-4 shadow-sm transition-all ${
                  logisticsConfig.request_date_enabled
                    ? 'border-emerald-200 bg-[linear-gradient(135deg,_#f3fff9_0%,_#ecfdf5_100%)]'
                    : 'border-slate-200 bg-slate-50'
                }`}>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                        Estado actual
                      </p>
                      <p className={`mt-2 text-lg font-black tracking-tight ${logisticsConfig.request_date_enabled ? 'text-emerald-700' : 'text-slate-700'}`}>
                        {logisticsConfig.request_date_enabled ? 'Campo habilitado' : 'Campo desactivado'}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => updateLogistics({ request_date_enabled: !logisticsConfig.request_date_enabled })}
                      className={`relative inline-flex h-9 w-[62px] shrink-0 items-center rounded-full border p-1 overflow-hidden transition-all ${
                        logisticsConfig.request_date_enabled
                          ? 'bg-[#0B2A36] border-[#0B2A36]'
                          : 'bg-white border-slate-300'
                      }`}
                      aria-label="Activar fecha del pedido"
                    >
                      <span
                        className={`h-7 w-7 rounded-full shadow-[0_8px_20px_rgba(15,23,42,0.18)] transition-transform duration-300 ${
                          logisticsConfig.request_date_enabled
                            ? 'translate-x-6 bg-[#3FF2AA]'
                            : 'translate-x-0 bg-slate-300'
                        }`}
                      />
                    </button>
                  </div>

                  <div className="mt-4 rounded-2xl border border-white/60 bg-white/70 px-3 py-3">
                    <p className="text-[8px] font-black uppercase tracking-widest text-slate-400">
                      Comportamiento
                    </p>
                    <p className="mt-2 text-[11px] leading-relaxed text-slate-500">
                      {logisticsConfig.request_date_enabled
                        ? 'Cada pedido muestra el selector en la tienda y en logistica, y se puede mover de dia sin salir del calendario.'
                        : 'Los pedidos se muestran usando la fecha de ingreso hasta que habilites la programacion operativa.'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <label className="block rounded-[24px] border border-slate-200 bg-slate-50 p-4">
                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                    Etiqueta del campo
                  </span>
                  <input
                    value={logisticsConfig.request_date_label}
                    onChange={(e) => updateLogistics({ request_date_label: e.target.value })}
                    className="mt-3 w-full bg-white border border-slate-200 rounded-2xl px-4 py-3 text-[12px] font-bold text-slate-700 outline-none focus:border-[#51A7FD]"
                    placeholder="Ej: Dia operativo"
                  />
                </label>

                <label className="block rounded-[24px] border border-slate-200 bg-slate-50 p-4">
                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                    Ayuda para operacion
                  </span>
                  <textarea
                    rows={3}
                    value={logisticsConfig.request_date_helper}
                    onChange={(e) => updateLogistics({ request_date_helper: e.target.value })}
                    className="mt-3 w-full resize-none bg-white border border-slate-200 rounded-2xl px-4 py-3 text-[12px] font-bold text-slate-700 outline-none focus:border-[#51A7FD]"
                    placeholder="Explica como usa logistica esta fecha"
                  />
                </label>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-[28px] border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 md:px-6 py-4 border-b border-slate-100 bg-[#0B2A36] flex items-center gap-2">
              <CalendarDays size={16} className="text-[#3FF2AA]" />
              <span className="font-black text-white text-[11px] uppercase tracking-widest">
                Preview logistica
              </span>
            </div>

            <div className="p-5 md:p-6 bg-slate-50 space-y-4">
              <div className="rounded-[24px] border border-slate-200 bg-white p-4 shadow-sm">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
                      Tarjeta de pedido
                    </p>
                    <h3 className="mt-2 text-sm font-black uppercase tracking-tight text-slate-800">
                      Pedido #WEB-2041
                    </h3>
                    <p className="text-[11px] font-bold text-slate-500">
                      Cliente web - 12 unidades
                    </p>
                  </div>
                  <span className={`px-3 py-1.5 rounded-full text-[9px] font-black uppercase tracking-widest ${
                    logisticsConfig.request_date_enabled
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-slate-100 text-slate-500 border border-slate-200'
                  }`}>
                    {logisticsConfig.request_date_enabled ? 'Editable' : 'Fallback'}
                  </span>
                </div>

                <div className="mt-4 rounded-[20px] border p-3.5 transition-all" style={{ borderColor: logisticsConfig.request_date_enabled ? '#bfdbfe' : '#e2e8f0' }}>
                  <div className="flex items-center gap-2 text-xs font-bold text-slate-700">
                    <CalendarDays size={14} className={logisticsConfig.request_date_enabled ? 'text-sky-600' : 'text-slate-300'} />
                    <span className="break-words">{logisticsConfig.request_date_label || 'Dia del pedido'}</span>
                  </div>

                  <div className={`mt-3 rounded-2xl border px-3 py-3 text-sm font-bold ${
                    logisticsConfig.request_date_enabled
                      ? 'border-sky-200 bg-sky-50 text-slate-700'
                      : 'border-slate-200 bg-slate-50 text-slate-500'
                  }`}>
                    {logisticsConfig.request_date_enabled ? '2026-04-03' : 'Se usa la fecha de ingreso del pedido'}
                  </div>

                  <p className="mt-3 text-[10px] leading-relaxed text-slate-400">
                    {logisticsConfig.request_date_enabled
                      ? logisticsConfig.request_date_helper
                      : 'El operador ve el pedido en el dia de ingreso hasta que lo reprograme.'}
                  </p>
                </div>
              </div>

              <div className="rounded-[24px] border border-dashed border-slate-200 bg-white/70 p-4">
                <p className="text-[9px] font-black uppercase tracking-widest text-slate-400">
                  Resultado esperado
                </p>
                <p className="mt-2 text-[12px] leading-relaxed text-slate-500">
                  Un control claro, sin desbordes visuales, con el switch contenido dentro de su tarjeta y una
                  preview consistente con el uso real en operacion.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .animate-nova-in { animation: novaFadeIn 0.28s cubic-bezier(0.16, 1, 0.3, 1) forwards; }
        @keyframes novaFadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
}
